// Privacy Policy and Terms of Service.
//
// These exist for a specific reason: US carriers (AT&T, T-Mobile, Verizon)
// require a working privacy policy and terms URL before they will approve
// business text messaging under A2P 10DLC. A reviewer opens these links by
// hand. A placeholder, a dead link, or a page that just loads the homepage is
// an automatic rejection — which is what we had, since the app has no router
// and every unknown URL served index.html.
//
// The clauses carriers specifically look for, and which must not be quietly
// deleted:
//   - "we do not sell or share mobile opt-in data with third parties"
//   - message frequency, "message and data rates may apply"
//   - how to get HELP and how to STOP
// Everything else is ordinary plain-English disclosure.
//
// Deliberately English-only. Legal text that's machine-translated is worse
// than legal text in one language the customer can ask us about.

import React from 'react';
import BRAND from '../brandConfig';
import { useApp } from '../context/AppContext';
import BrandMark from '../components/BrandMark';

const CONTACT_EMAIL = 'support@qpshub.com';
const CONTACT_PHONE = '(385) 338-3625';
const LAST_UPDATED = 'August 3, 2026';

// Shared chrome so both documents scroll and navigate identically.
function LegalPage({ screen, title, children }) {
  const { currentScreen, goTo } = useApp();
  return (
    <div className={`screen ${currentScreen === screen ? 'active' : ''}`}>
      <nav className="nav-public">
        <BrandMark />
        <button className="btn btn-ghost btn-sm" onClick={() => goTo('screen-home')}>Back</button>
      </nav>
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }} className="pb">
        {/* A real h1. The page had none, so a screen reader landing here was
            told nothing about what document it had opened. */}
        <h1 className="section-title" style={{ margin: 0 }}>{title}</h1>
        <div className="section-sub">Last updated {LAST_UPDATED}</div>
        <div style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--slate)', maxWidth: 720 }}>
          {children}
          <div className="divider"></div>
          <p style={{ fontSize: 12, color: 'var(--muted)' }}>
            {BRAND.name.full} · {BRAND.serviceArea.regionLabel}, Utah<br />
            {CONTACT_EMAIL} · {CONTACT_PHONE}
          </p>
        </div>
      </div>
    </div>
  );
}

const H = ({ children }) => (
  <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)', margin: '22px 0 8px' }}>{children}</h3>
);

export function PrivacyPolicy() {
  return (
    <LegalPage screen="screen-privacy" title="Privacy Policy">
      <p>
        {BRAND.name.full} ("we", "us") provides home repair, maintenance and remodeling services in Utah.
        This policy explains what personal information we collect, why we collect it, and who can see it.
      </p>

      <H>What we collect</H>
      <p>
        When you submit a service request we collect your name, phone number, email address, service address,
        and the description and photos or videos of the work you are asking us to do. If we perform work for
        you we also keep records of estimates, invoices, payments and messages related to your job.
      </p>
      <p>
        Technicians who apply to work with us provide their name, phone number, email address, business
        details, licensing and insurance information.
      </p>

      <H>Why we collect it</H>
      <p>
        We use this information to quote your job, schedule and perform the work, communicate with you and
        the technician assigned to your job, send invoices and receipts, and keep a record of the work for
        warranty and dispute purposes. We do not use it to advertise to you.
      </p>

      <H>Text messages and mobile information</H>
      <p>
        If you give us your mobile number and agree to text messaging, we use it to send you messages about
        your specific job — scheduling, technician arrival, job progress, repairs needing your approval, and
        completion. Your replies are saved to your job record so the full conversation stays in one place.
      </p>
      <p style={{ fontWeight: 600 }}>
        We do not sell, rent, or share mobile opt-in information or phone numbers with third parties or
        affiliates for marketing or promotional purposes. Text message consent is never shared with anyone.
      </p>
      <p>
        Message frequency varies with the activity on your job. Message and data rates may apply. Reply STOP
        to any message to stop receiving texts, or HELP for help. See our Terms of Service for the full
        messaging terms.
      </p>

      <H>Who we share information with</H>
      <p>
        We share only what is necessary, and only with:
      </p>
      <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
        <li>The technician assigned to your job — your name, address, phone number and job details, so they can do the work.</li>
        <li>Service providers that operate this site on our behalf: our hosting provider, our database provider, our photo storage provider, our email provider, and our text messaging provider. They may only process the data to provide that service to us.</li>
        <li>Anyone we are legally required to share it with.</li>
      </ul>
      <p>We do not sell your personal information.</p>

      <H>Cookies and what your browser stores</H>
      <p>
        We do not use advertising cookies, tracking pixels, or third-party analytics. Nothing on this site
        follows you anywhere else, and we do not sell or share browsing data with anyone.
      </p>
      <p>
        The site does keep a few things in your own browser so it works: your sign-in so you are not asked
        for your password on every screen, your language, and whether you chose light or dark. That stays on
        your device, is never sent anywhere for advertising, and clearing your browser data removes it.
      </p>

      <H>How long we keep it</H>
      <p>
        We keep job records for as long as needed for warranty, tax, and legal purposes. You may ask us to
        delete your account and personal information at any time by emailing {CONTACT_EMAIL}, except where we
        are required to keep records.
      </p>

      <H>Your choices</H>
      <p>
        You can ask us what information we hold about you, ask us to correct it, ask us to delete it, or stop
        text messages at any time. Email {CONTACT_EMAIL} or call {CONTACT_PHONE}.
      </p>

      <H>Children</H>
      <p>This site is not directed to children under 13 and we do not knowingly collect their information.</p>

      <H>Changes</H>
      <p>
        If we change this policy we will update the date at the top of this page.
      </p>
    </LegalPage>
  );
}

export function Terms() {
  return (
    <LegalPage screen="screen-terms" title="Terms of Service">
      <p>
        These terms apply to your use of this website and to home repair, maintenance and remodeling services
        provided by {BRAND.name.full}.
      </p>

      <H>Service requests and estimates</H>
      <p>
        Submitting a request through this site is not a contract. It asks us to look at your job. Any estimate
        we provide is based on the information and photos you give us and on what is visible at the time. Work
        that could not reasonably be seen beforehand — for example damage behind a wall — may change the price,
        and we will tell you and get your approval before doing it.
      </p>

      <H>Scheduling and payment</H>
      <p>
        Scheduled dates are our best estimate and can move for weather, permits, parts availability or
        emergencies. Payment terms are stated on your estimate or invoice. Work may be paused if an invoice
        is past due.
      </p>

      <H>Cancelling, and getting money back</H>
      <p>
        You can cancel a scheduled visit at any time before it starts by calling or emailing us, and there is
        no charge for a visit we have not made. Once work has started, you pay for the work performed and for
        materials already bought for your job.
      </p>
      <p>
        If something we did is not right, tell us and we will come back and put it right. If we cannot, we
        will refund what you paid for that part of the work. Ask us within 30 days of the invoice date.
        Refunds go back the way you paid and we start them within 10 business days of agreeing one.
      </p>
      <p>
        We do not hold your money in advance. Nothing is charged when you approve an estimate — you are
        invoiced after the work is done, on the terms printed on that invoice.
      </p>

      <H>Text messaging program</H>
      <p>
        {BRAND.name.full} operates a text messaging program so you and the technician working on your job can
        reach each other directly, with the conversation saved to your account.
      </p>
      <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
        <li><strong>What you get:</strong> messages about your specific job — scheduling, arrival times, job progress, repairs needing approval, and completion notices. We do not send marketing or promotional texts.</li>
        <li><strong>How you sign up:</strong> by checking the text messaging box when you submit a service request, or when you create a technician account. Consent to receive texts is not a condition of purchasing anything from us.</li>
        <li><strong>Message frequency:</strong> varies with activity on your job. Most jobs generate a handful of messages; an active job may generate more.</li>
        <li><strong>Cost:</strong> we do not charge for messages. Message and data rates may apply from your carrier.</li>
        <li><strong>To stop:</strong> reply <strong>STOP</strong> to any message and we will stop texting that number. You will get one confirmation message. You can still reach us by phone and email, and you can still use your portal.</li>
        <li><strong>To get help:</strong> reply <strong>HELP</strong> to any message, email {CONTACT_EMAIL}, or call {CONTACT_PHONE}.</li>
        <li><strong>Carriers:</strong> carriers are not liable for delayed or undelivered messages.</li>
      </ul>

      <H>Photos you upload</H>
      <p>
        You keep ownership of photos and videos you upload. You give us permission to use them to quote and
        perform your job and to keep them in your job record. We will not publish them publicly or use them in
        advertising without asking you first.
      </p>

      <H>Your account</H>
      <p>
        Keep your password private. You are responsible for activity under your account. Tell us right away at
        {' '}{CONTACT_EMAIL} if you think someone else has access to it.
      </p>

      <H>Technicians</H>
      <p>
        Technicians who take work through this site are independent contractors and are responsible for
        holding their own licensing and insurance, and for keeping it current.
      </p>

      <H>Limits</H>
      <p>
        This website is provided as-is. We are not liable for indirect or consequential damages arising from
        use of the site. Nothing here limits any warranty we give you in writing on work we actually perform,
        or any right you have under Utah law.
      </p>

      <H>Governing law</H>
      <p>These terms are governed by the laws of the State of Utah.</p>

      <H>Contact</H>
      <p>{CONTACT_EMAIL} · {CONTACT_PHONE}</p>
    </LegalPage>
  );
}
